import { useEffect } from 'react';

/**
 * Hook to automatically focus the first input or textarea in a modal or drawer
 * Works with both standalone and nested modals/drawers
 * 
 * @param {Object} options
 * @param {number} options.delay - Delay in ms before focusing (default: 100ms)
 * @param {string} options.selector - Custom selector to focus (overrides default behavior)
 * @param {boolean} options.focusTextareas - Whether to include textareas (default: true)
 * @param {boolean} options.focusSelects - Whether to include select elements (default: false)
 */
export const useAutoFocusFirstInput = (options = {}) => {
  const {
    delay = 100,
    selector = null,
    focusTextareas = true,
    focusSelects = false,
  } = options;

  useEffect(() => {
    const timer = setTimeout(() => {
      // Try custom selector if provided
      if (selector) {
        const element = document.querySelector(selector);
        if (element) {
          element.focus();
          return;
        }
      }

      // Try to find the active container
      // For modals we need ant-modal-wrap, for drawers we need ant-drawer
      const visibleModals = Array.from(document.querySelectorAll('.ant-modal-wrap:not(.ant-modal-hidden)'));
      const visibleDrawers = Array.from(document.querySelectorAll('.ant-drawer-open'));
      
      const allContainers = [...visibleModals, ...visibleDrawers];
      
      // No visible containers found
      if (allContainers.length === 0) return;
      
      // Get the z-index of a container
      const getZIndex = (container) => {
        return parseInt(window.getComputedStyle(container).zIndex, 10) || 0;
      };
      
      // Sort containers by z-index (highest first)
      const sortedContainers = allContainers.sort((a, b) => getZIndex(b) - getZIndex(a));
      
      // Topmost container is the one with highest z-index
      const topmostContainer = sortedContainers[0];
      
      // For modals, use the modal body; for drawers, use the drawer body
      const containerBody = topmostContainer.classList.contains('ant-modal-wrap') 
        ? topmostContainer.querySelector('.ant-modal-body') 
        : topmostContainer.querySelector('.ant-drawer-body');
        
      if (!containerBody) return;
      
      // Three-pass approach:
      
      // 1. First priority: Try to find name/title inputs (which usually come first in forms)
      const nameInput = containerBody.querySelector(
        'input[id*="name"]:not([disabled]):not([readonly]), ' +
        'input[name*="name"]:not([disabled]):not([readonly]), ' +
        'input[id*="title"]:not([disabled]):not([readonly]), ' +
        'input[name*="title"]:not([disabled]):not([readonly]), ' +
        'input[placeholder*="Name"]:not([disabled]):not([readonly]), ' +
        'input[placeholder*="Title"]:not([disabled]):not([readonly])'
      );
      
      if (nameInput) {
        nameInput.focus();
        return;
      }
      
      // 2. Second priority: For forms focused on reasoning/descriptions
      if (focusTextareas) {
        const reasoningTextarea = containerBody.querySelector(
          'textarea[id*="reason"]:not([disabled]):not([readonly]), ' +
          'textarea[name*="reason"]:not([disabled]):not([readonly]), ' +
          'textarea[id*="description"]:not([disabled]):not([readonly]), ' +
          'textarea[name*="description"]:not([disabled]):not([readonly])'
        );
        
        if (reasoningTextarea) {
          reasoningTextarea.focus();
          return;
        }
      }
      
      // 3. Then try form items in order (skipping checkboxes, switches, etc)
      const formItems = containerBody.querySelectorAll('.ant-form-item');
      
      for (const formItem of formItems) {
        // Skip form items with controls
        if (formItem.querySelector('.ant-checkbox, .ant-radio, .ant-switch')) {
          continue;
        }
        
        // Find inputs within the form item
        const inputs = [];
        
        // Regular inputs (text, email, password, etc)
        const textInputs = formItem.querySelectorAll('input:not([type="hidden"]):not([disabled]):not([readonly])');
        inputs.push(...textInputs);
        
        // Textareas if enabled
        if (focusTextareas) {
          const textareas = formItem.querySelectorAll('textarea:not([disabled]):not([readonly])');
          inputs.push(...textareas);
        }
        
        // Selects if enabled
        if (focusSelects) {
          const selects = formItem.querySelectorAll('select:not([disabled])');
          inputs.push(...selects);
        }
        
        // Focus the first input if found
        if (inputs.length > 0) {
          inputs[0].focus();
          return;
        }
      }
      
      // 4. Fallback to any input/textarea if we couldn't find anything above
      const fallbackSelectors = [];
      
      // Regular inputs
      fallbackSelectors.push('input:not([type="hidden"]):not([disabled]):not([readonly])');
      
      // Add textareas if enabled
      if (focusTextareas) {
        fallbackSelectors.push('textarea:not([disabled]):not([readonly])');
      }
      
      // Add selects if enabled
      if (focusSelects) {
        fallbackSelectors.push('select:not([disabled])');
      }
      
      const fallbackElements = containerBody.querySelectorAll(fallbackSelectors.join(','));
      
      if (fallbackElements.length > 0) {
        // Filter out helper elements (those with position: absolute and bottom: 0)
        const realElements = Array.from(fallbackElements).filter(el => {
          const style = window.getComputedStyle(el);
          return !(style.position === 'absolute' && style.bottom === '0px');
        });
        
        if (realElements.length > 0) {
          realElements[0].focus();
        }
      }
    }, delay);
    
    return () => clearTimeout(timer);
  }, [selector, delay, focusTextareas, focusSelects]);
}; 